import { useEffect } from 'react';
import { AlertTriangle, CheckCircle, Mail, Clipboard, ArrowRight } from 'lucide-react';
import { useEventTracking } from '../hooks/useEventTracking';
import {Helmet} from "react-helmet-async";
import {CONTACT_DETAILS} from "@/config/contactDetails.ts";

export const MaintenanceSignsPage = () => {
    const { trackPageView, trackClick } = useEventTracking();

    // Track page view
    useEffect(() => {
        trackPageView('/5-signs-maintenance-strategy', document.referrer);
    }, [trackPageView]);

    const handleSectionClick = (sectionName: string) => {
        trackClick(`maintenance-signs-${sectionName}`, `5 Signs Page - ${sectionName}`, 'section');
    };

    const handleCTAClick = (ctaType: string) => {
        trackClick(`maintenance-signs-cta-${ctaType}`, `5 Signs Page CTA - ${ctaType}`, 'button');
    };

    return (
        <>
            <Helmet>
                <title>{`5 Signs Your Maintenance Strategy is Costing You Money | ${CONTACT_DETAILS.company.name} Guide`}</title>
                <meta name="description" content="Discover the 5 warning signs that your maintenance approach is draining profits. Learn how predictive maintenance can reduce downtime by 75% and cut costs by 25%." />
                <meta name="keywords" content="maintenance strategy problems, reactive maintenance costs, predictive maintenance benefits, equipment failure prevention, maintenance optimization" />
                <link rel="canonical" href="https://4castlabs.com/5-signs-maintenance-strategy" />
                <meta property="og:title" content={`5 Signs Your Maintenance Strategy is Costing You Money | ${CONTACT_DETAILS.company.name}`} />
                <meta property="og:description" content="Discover the 5 warning signs that your maintenance approach is draining profits. Learn how predictive maintenance can reduce downtime and cut costs." />
                <meta property="og:url" content="https://4castlabs.com/5-signs-maintenance-strategy" />
                <meta property="og:type" content="article" />
                <meta property="og:image" content="https://4castlabs.com/images/logo.png" />
            </Helmet>
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
                <div className="max-w-4xl mx-auto px-6 py-12">
                    {/* Hero Section */}
                    <div className="text-center mb-16">
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                            5 Signs Your Maintenance Strategy
                            <span className="text-red-600"> is Costing You</span>
                        </h1>
                        <p className="text-xl text-gray-600 mb-8">
                            (and What to Do About It)
                        </p>

                        {/* Warning Callout */}
                        <div
                            className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg max-w-3xl mx-auto cursor-pointer hover:shadow-md transition-shadow"
                            onClick={() => handleSectionClick('warning-callout')}
                        >
                            <div className="flex items-center">
                                <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0" />
                                <p className="text-lg font-semibold text-yellow-800">
                                    Companies lose an average of $50,000 per hour during unplanned downtime.
                                    Is your maintenance strategy preventing this... or causing it?
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Signs Grid */}
                    <div className="grid md:grid-cols-2 gap-8 mb-16">
                        {[
                            {
                                number: 1,
                                title: "Reactive 'Fix When Broken' Mentality",
                                description: "You're constantly firefighting equipment failures instead of preventing them. Emergency repairs cost 3-5x more than planned maintenance.",
                                impact: "High emergency repair costs"
                            },
                            {
                                number: 2,
                                title: "Calendar-Based Maintenance Only",
                                description: "Replacing parts 'just in case' wastes money on premature replacements while missing actual failure indicators.",
                                impact: "Wasted parts inventory"
                            },
                            {
                                number: 3,
                                title: "No Real-Time Equipment Monitoring",
                                description: "Without continuous monitoring, you're flying blind. Critical issues develop undetected until catastrophic failure occurs.",
                                impact: "Surprise equipment failures"
                            },
                            {
                                number: 4,
                                title: "Maintenance Data Lives in Silos",
                                description: "Disconnected systems prevent you from seeing patterns, trends, and optimization opportunities across your operations.",
                                impact: "Missed optimization opportunities"
                            }
                        ].map((sign) => (
                            <div
                                key={sign.number}
                                className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-red-500 hover:shadow-xl transition-shadow cursor-pointer"
                                onClick={() => handleSectionClick(`sign-${sign.number}`)}
                            >
                                <div className="flex items-center mb-4">
                                    <div className="bg-red-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg mr-4">
                                        {sign.number}
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">{sign.title}</h3>
                                </div>
                                <p className="text-gray-700 leading-relaxed mb-3">{sign.description}</p>
                                <div className="text-sm font-semibold text-red-600 bg-red-50 px-3 py-1 rounded-full inline-block">
                                    💸 {sign.impact}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Fifth Sign - Full Width */}
                    <div
                        className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-red-500 mb-16 hover:shadow-xl transition-shadow cursor-pointer"
                        onClick={() => handleSectionClick('sign-5')}
                    >
                        <div className="flex items-center mb-4">
                            <div className="bg-red-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg mr-4">
                                5
                            </div>
                            <h3 className="text-xl font-bold text-gray-900">High Inventory Costs with Frequent Stockouts</h3>
                        </div>
                        <p className="text-gray-700 leading-relaxed mb-3">
                            You're either hoarding expensive spare parts "just in case" or scrambling to find critical components during emergencies. Both scenarios drain profits.
                        </p>
                        <div className="text-sm font-semibold text-red-600 bg-red-50 px-3 py-1 rounded-full inline-block">
                            💸 Poor inventory management
                        </div>
                    </div>

                    {/* Solution Section */}
                    <div
                        className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-2xl p-10 mb-16 cursor-pointer hover:shadow-xl transition-shadow"
                        onClick={() => handleSectionClick('solution-section')}
                    >
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold mb-4">The Solution: Predictive Maintenance</h2>
                            <p className="text-xl text-blue-100 leading-relaxed">
                                Transform from reactive to proactive with AI-powered predictive maintenance that
                                monitors equipment health in real-time and predicts failures before they happen.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            {[
                                "Reduce unplanned downtime by 75%*",
                                "Cut maintenance costs by 25%*",
                                "Extend equipment life by 20%*",
                                "Optimize inventory levels"
                            ].map((benefit, index) => (
                                <div key={index} className="flex items-center">
                                    <CheckCircle className="h-6 w-6 text-green-400 mr-3 flex-shrink-0"/>
                                    <span className="text-lg">{benefit}</span>
                                </div>
                            ))}
                        </div>
                        <div className="text-center text-xs text-blue-200 mt-4">
                            *Based on industry studies
                        </div>
                    </div>

                    {/* Pilot Program Section */}
                    <div
                        className="bg-gradient-to-r from-green-600 to-green-800 text-white rounded-2xl p-10 mb-16 cursor-pointer hover:shadow-xl transition-shadow"
                        onClick={() => handleSectionClick('pilot-program')}
                    >
                        <div className="text-center mb-6">
                            <h2 className="text-3xl font-bold mb-4">Start Small, Scale Smart</h2>
                            <p className="text-xl text-green-100">
                                Not ready for multiple assets? Try the {CONTACT_DETAILS.company.name} pilot program on just one critical machine.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6 mb-8">
                            <div className="text-center">
                                <div className="bg-green-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                                    <span className="text-white font-bold">1</span>
                                </div>
                                <h3 className="font-semibold mb-2">Choose One Machine</h3>
                                <p className="text-sm text-green-100">Pick your most critical or expensive equipment</p>
                            </div>
                            <div className="text-center">
                                <div className="bg-green-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                                    <span className="text-white font-bold">2</span>
                                </div>
                                <h3 className="font-semibold mb-2">3-6 Month Trial</h3>
                                <p className="text-sm text-green-100">Full setup, monitoring, and predictive insights</p>
                            </div>
                            <div className="text-center">
                                <div className="bg-green-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                                    <span className="text-white font-bold">3</span>
                                </div>
                                <h3 className="font-semibold mb-2">Proven ROI</h3>
                                <p className="text-sm text-green-100">Scale to more equipment or walk away</p>
                            </div>
                        </div>

                        <div className="text-center">
                            <a
                                href="/contact?source=pilot-program"
                                className="bg-white text-green-700 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-colors inline-flex items-center"
                                onClick={() => handleCTAClick('pilot-program')}
                            >
                                Ask About the Pilot Program
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </a>
                        </div>
                    </div>

                    {/* FAQ Section */}
                    <div className="bg-white rounded-2xl p-10 shadow-lg mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                            Frequently Asked Questions
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {[
                                {
                                    q: "What does a predictive maintenance assessment cost?",
                                    a: `The initial assessment is completely free. ${CONTACT_DETAILS.company.name} analyzes your specific equipment and provides a custom ROI projection. Pilot programs are individually priced based on your specific needs.`
                                },
                                {
                                    q: "How long does a typical pilot project take?",
                                    a: "Most pilots run 3-6 months on a single machine. Week 1-2: Setup and data collection. Month 1-2: Baseline establishment. Month 3-6: Predictive model training and alert delivery."
                                },
                                {
                                    q: "What's required from our team?",
                                    a: "Minimal disruption to operations. Requirements include: equipment access for setup, historical maintenance records (if available), and a technical contact person (2-3 hours/week)."
                                },
                                {
                                    q: "How do you prove ROI during the pilot?",
                                    a: `${CONTACT_DETAILS.company.name} tracks specific KPIs: equipment uptime, maintenance costs, parts inventory optimization, and early failure detection events. Baseline metrics are established before the pilot begins.`
                                },
                                {
                                    q: "What happens after a successful pilot?",
                                    a: "Successful pilots can scale gradually. Most operations expand to monitor 2-3 additional critical machines over 12 months, with full facility coverage in year 2."
                                },
                                {
                                    q: "Do you work with older equipment?",
                                    a: `Yes, older equipment often benefits most from predictive maintenance. ${CONTACT_DETAILS.company.name} can work with any equipment that has basic electrical monitoring points.`
                                }
                            ].map((faq, index) => (
                                <div
                                    key={index}
                                    className="bg-gray-50 p-6 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                                    onClick={() => handleSectionClick(`faq-${index + 1}`)}
                                >
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.q}</h3>
                                    <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* CTA Section */}
                    <div className="bg-white rounded-2xl p-10 shadow-xl text-center">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            Ready to Stop Costly Maintenance Mistakes?
                        </h2>
                        <p className="text-xl text-gray-600 mb-8">
                            Get a free assessment of your current maintenance strategy and discover your potential savings.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <a
                                href="/assessment"
                                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors flex items-center group"
                                onClick={() => handleCTAClick('assessment')}
                            >
                                <Clipboard className="mr-2 h-5 w-5" />
                                Take Free Assessment
                                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                            </a>

                            <a
                                href="/contact?source=5-signs-guide"
                                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors flex items-center group"
                                onClick={() => handleCTAClick('contact-form')}
                            >
                                <Mail className="mr-2 h-5 w-5" />
                                Schedule Call
                                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                            </a>
                        </div>

                        <p className="text-gray-500 mt-6">
                            Free consultation • No commitment • Immediate insights
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MaintenanceSignsPage;